import { CalendarCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { cn } from '@/lib/utils'
import { dayHeader, quietSpan, type AgendaEntry } from './agenda'
import { DayListRowButton } from './day-list-row'

interface AgendaViewProps {
  entries: AgendaEntry[]
  /** The property's own today, as the read reported it; null until it has. */
  today: string | null
  /** The month has not been read yet (first load, or paging): a skeleton, never "nothing". */
  loading: boolean
  /**
   * Whether a run of empty days is "nothing scheduled". False when a layer
   * failed or is switched off: the days are then empty of what could be read.
   */
  claimNothing: boolean
  /** Said when no day has anything on it. */
  emptyMessage: string
  /** The phone has no day cells to tap, so "Block dates" is a button of the list itself. */
  onBlockDates?: () => void
}

/**
 * The month as a list of days on a phone. Read-only like the grid: each row
 * opens the sheet of the thing it names, through the same `open=` value.
 */
export function AgendaView({
  entries,
  today,
  loading,
  claimNothing,
  emptyMessage,
  onBlockDates,
}: AgendaViewProps) {
  const blockButton = onBlockDates ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onBlockDates}
      data-testid="agenda-block-dates"
    >
      <CalendarCheck className="mr-1.5 h-4 w-4" aria-hidden />
      Block dates
    </Button>
  ) : null
  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true" data-testid="agenda-loading">
        <LoadingSkeleton className="h-6 w-1/3" />
        <LoadingSkeleton className="h-14 w-full" />
        <LoadingSkeleton className="h-14 w-full" />
        <LoadingSkeleton className="h-6 w-1/3" />
        <LoadingSkeleton className="h-14 w-full" />
      </div>
    )
  }
  const hasDays = entries.some(entry => entry.kind === 'day')
  if (!hasDays) {
    return (
      <div className="space-y-3">
        {blockButton}
        <p className="py-6 text-center text-sm text-textMuted" data-testid="agenda-empty">
          {emptyMessage}
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-3" data-testid="calendar-agenda">
      {blockButton}
      {entries.map(entry => {
        if (entry.kind === 'quiet') {
          return (
            <p
              key={`quiet:${entry.from}`}
              className="px-1 text-xs text-textFaint"
              data-testid="agenda-quiet"
            >
              {quietSpan(entry)}: {claimNothing ? 'nothing scheduled' : 'nothing to show'}
            </p>
          )
        }
        const header = dayHeader(entry.day)
        const isToday = entry.day === today
        return (
          <section
            key={entry.day}
            aria-label={header.long}
            data-testid="agenda-day"
            data-day={entry.day}
            className="space-y-1.5"
          >
            <h3
              className={cn(
                'sticky top-0 z-10 flex items-center gap-2 bg-surface py-1.5 text-sm font-semibold',
                isToday ? 'text-mint' : 'text-text'
              )}
            >
              <span aria-hidden="true">{header.short}</span>
              <span className="sr-only">{header.long}</span>
              {isToday && (
                <span
                  className="rounded-full border border-mint bg-mint/15 px-2 py-0.5 text-[11px] font-medium text-mint"
                  data-testid="agenda-today"
                >
                  Today
                </span>
              )}
            </h3>
            <ul className="space-y-1.5">
              {entry.rows.map(row => (
                <li key={row.key}>
                  <DayListRowButton row={row} />
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
